export type ExpertCheckoutStatus =
  | "checkout_pending"
  | "incomplete"
  | "incomplete_expired"
  | "trialing"
  | "active"
  | "past_due"
  | "unpaid"
  | "paused"
  | "canceled";

export interface ExpertBillingPlanSummary {
  id: number;
  name: string;
  slug: string;
}

export interface ExpertBillingOptionTerms {
  id: number;
  label?: string;
  amount_cents: number;
  currency: string;
  interval_unit: "week" | "month" | "year";
  interval_count: number;
}

export interface ExpertOfferTerms {
  plan?: ExpertBillingPlanSummary | null;
  billing_option?: ExpertBillingOptionTerms | null;
}

export interface ExpertCheckoutSummary extends ExpertOfferTerms {
  id: number;
  invitation_id: number;
  status: ExpertCheckoutStatus;
  mode: "test" | "live";
  checkout_session_id: string | null;
  checkout_url: string | null;
  checkout_expires_at: string | null;
  checkout_attempt: number;
  trial_ends_at: string | null;
  created_at: string | null;
}

export interface ExpertInvitationOfferPreview extends ExpertOfferTerms {
  invitation: {
    id: number;
    mode: "test" | "live";
    expires_at: string | null;
  };
  plan: ExpertBillingPlanSummary;
  billing_option: ExpertBillingOptionTerms & { label: string };
}

type BillingRedemptionErrorCode =
  | "billing_invitation_not_found"
  | "billing_invitation_not_authorized"
  | "billing_invitation_unavailable"
  | "billing_invitation_mode_mismatch"
  | "billing_invitation_plan_unavailable"
  | "billing_invitation_option_unavailable"
  | "billing_invitation_price_unavailable"
  | "billing_invitation_expiry_too_close"
  | "billing_checkout_wait_for_paid_period"
  | "billing_provider_subscription_exists"
  | "billing_checkout_organization_missing"
  | "billing_checkout_active"
  | "billing_checkout_provider_failed";

export const billingInvitationFragmentKey = "billing-invitation";
export const billingCheckoutReturnKey = "billing_checkout";
export const billingCheckoutSessionKey = "session_id";

let heldBillingInvitationToken: string | null = null;

export function holdBillingInvitationTokenForLogin(token: string): void {
  heldBillingInvitationToken = token.trim() || null;
}

export function consumeHeldBillingInvitationToken(): string | null {
  const token = heldBillingInvitationToken;
  heldBillingInvitationToken = null;
  return token;
}

export type BillingCheckoutReturnState =
  | { kind: "none" }
  | { kind: "success"; sessionId: string | null }
  | { kind: "cancelled" };

export interface CapturedExpertCheckoutLocation {
  invitationToken: string | null;
  returnState: BillingCheckoutReturnState;
  cleanRelativeUrl: string;
  changed: boolean;
}

function fragmentParams(hash: string): URLSearchParams {
  return new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
}

export function parseBillingInvitationToken(hash: string): string | null {
  const token = fragmentParams(hash).get(billingInvitationFragmentKey)?.trim();
  return token || null;
}

export function stripBillingInvitationFragment(hash: string): string {
  if (!hash) return "";
  const params = fragmentParams(hash);
  if (!params.has(billingInvitationFragmentKey)) {
    return hash.startsWith("#") ? hash : `#${hash}`;
  }
  params.delete(billingInvitationFragmentKey);
  const next = params.toString();
  return next ? `#${next}` : "";
}

export function stripBillingInvitationFromFullPath(fullPath: string): string {
  const hashIndex = fullPath.indexOf("#");
  if (hashIndex < 0) return fullPath;
  const pathAndQuery = fullPath.slice(0, hashIndex);
  return `${pathAndQuery}${stripBillingInvitationFragment(fullPath.slice(hashIndex))}`;
}

export function getSafeExpertCheckoutRedirect(
  roleId: number | undefined,
  redirect: unknown,
): string | null {
  if (roleId !== 3 || typeof redirect !== "string" || redirect.startsWith("//")) return null;

  try {
    const parsed = new URL(redirect, "https://dashboard.local");
    if (parsed.origin !== "https://dashboard.local" || parsed.pathname !== "/expert/settings") {
      return null;
    }
    return stripBillingInvitationFromFullPath(`${parsed.pathname}${parsed.search}${parsed.hash}`);
  } catch {
    return null;
  }
}

export function parseBillingCheckoutReturn(search: string): BillingCheckoutReturnState {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const state = params.get(billingCheckoutReturnKey);

  if (state === "success") {
    return {
      kind: "success",
      sessionId: params.get(billingCheckoutSessionKey)?.trim() || null,
    };
  }
  if (state === "cancelled") return { kind: "cancelled" };
  return { kind: "none" };
}

export function stripBillingCheckoutReturn(search: string): string {
  if (!search) return "";
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  params.delete(billingCheckoutReturnKey);
  params.delete(billingCheckoutSessionKey);
  const next = params.toString();
  return next ? `?${next}` : "";
}

export function captureExpertCheckoutLocation(location: {
  pathname: string;
  search: string;
  hash: string;
}): CapturedExpertCheckoutLocation {
  const cleanSearch = stripBillingCheckoutReturn(location.search);
  const cleanHash = stripBillingInvitationFragment(location.hash);

  return {
    invitationToken: parseBillingInvitationToken(location.hash),
    returnState: parseBillingCheckoutReturn(location.search),
    cleanRelativeUrl: `${location.pathname}${cleanSearch}${cleanHash}`,
    changed: cleanSearch !== location.search || cleanHash !== location.hash,
  };
}

export type PendingCheckoutState =
  | { kind: "none" }
  | { kind: "resumable"; checkout: ExpertCheckoutSummary }
  | { kind: "expired"; checkout: ExpertCheckoutSummary }
  | { kind: "unavailable"; checkout: ExpertCheckoutSummary };

export function getPendingCheckoutState(
  checkout: ExpertCheckoutSummary | null | undefined,
  now = Date.now(),
): PendingCheckoutState {
  if (!checkout) return { kind: "none" };

  const expiresAt = checkout.checkout_expires_at
    ? new Date(checkout.checkout_expires_at).getTime()
    : Number.NaN;

  if (Number.isFinite(expiresAt) && expiresAt <= now) {
    return { kind: "expired", checkout };
  }
  if (checkout.checkout_url && Number.isFinite(expiresAt) && expiresAt > now) {
    return { kind: "resumable", checkout };
  }
  return { kind: "unavailable", checkout };
}

export interface BillingRedemptionErrorDisplay {
  kind: "feature-unavailable" | "terminal" | "wait" | "retryable";
  title: string;
  message: string;
  action: "none" | "retry" | "refresh";
  retryAt: string | null;
}

interface BillingErrorResponse {
  status: number | null;
  code: BillingRedemptionErrorCode | null;
  message: string | null;
  retryAt: string | null;
}

const billingErrorCodes = new Set<BillingRedemptionErrorCode>([
  "billing_invitation_not_found",
  "billing_invitation_not_authorized",
  "billing_invitation_unavailable",
  "billing_invitation_mode_mismatch",
  "billing_invitation_plan_unavailable",
  "billing_invitation_option_unavailable",
  "billing_invitation_price_unavailable",
  "billing_invitation_expiry_too_close",
  "billing_checkout_wait_for_paid_period",
  "billing_provider_subscription_exists",
  "billing_checkout_organization_missing",
  "billing_checkout_active",
  "billing_checkout_provider_failed",
]);

function parseBillingErrorResponse(error: unknown): BillingErrorResponse {
  if (!error || typeof error !== "object") {
    return {
      status: null,
      code: null,
      message: error instanceof Error ? error.message : null,
      retryAt: null,
    };
  }

  const response = (error as { response?: { status?: unknown; data?: unknown } }).response;
  const status = typeof response?.status === "number" ? response.status : null;
  const data = response?.data;
  if (!data || typeof data !== "object") {
    return {
      status,
      code: null,
      message: error instanceof Error ? error.message : null,
      retryAt: null,
    };
  }

  const body = data as { error_code?: unknown; message?: unknown; retry_at?: unknown };
  const code = typeof body.error_code === "string" && billingErrorCodes.has(body.error_code as BillingRedemptionErrorCode)
    ? body.error_code as BillingRedemptionErrorCode
    : null;

  return {
    status,
    code,
    message: typeof body.message === "string" && body.message ? body.message : null,
    retryAt: typeof body.retry_at === "string" && body.retry_at ? body.retry_at : null,
  };
}

function terminal(title: string, message: string): BillingRedemptionErrorDisplay {
  return { kind: "terminal", title, message, action: "none", retryAt: null };
}

export function mapBillingRedemptionError(error: unknown): BillingRedemptionErrorDisplay {
  const details = parseBillingErrorResponse(error);

  // EnsureFeatureEnabled deliberately hides the endpoint with a code-less 404.
  if (details.status === 404 && !details.code) {
    return {
      kind: "feature-unavailable",
      title: "Subscription checkout is unavailable",
      message: "This subscription feature is not available right now. Please contact ShopExperts for help.",
      action: "none",
      retryAt: null,
    };
  }

  switch (details.code) {
    case "billing_invitation_not_found":
      return terminal(
        "Invitation not found",
        "This payment invitation is invalid. Ask ShopExperts to send you a new invitation.",
      );
    case "billing_invitation_not_authorized":
      return terminal(
        "Invitation belongs to another account",
        "Sign in with the expert account that received this invitation, or ask ShopExperts for a new link.",
      );
    case "billing_invitation_unavailable":
      return terminal(
        "Invitation is no longer available",
        "This invitation was used, replaced, cancelled, or expired. Ask ShopExperts to send you a new one.",
      );
    case "billing_invitation_mode_mismatch":
      return terminal(
        "Invitation can’t be used",
        "The billing environment changed after this invitation was issued. Ask ShopExperts for a new invitation.",
      );
    case "billing_invitation_plan_unavailable":
    case "billing_invitation_option_unavailable":
    case "billing_invitation_price_unavailable":
      return terminal(
        "Subscription offer is no longer available",
        "The plan or price attached to this invitation changed. Ask ShopExperts to send you an updated invitation.",
      );
    case "billing_invitation_expiry_too_close":
      return terminal(
        "Invitation is about to expire",
        "There isn’t enough time to complete a secure checkout. Ask ShopExperts to send you a new invitation.",
      );
    case "billing_checkout_wait_for_paid_period":
      return {
        kind: "wait",
        title: "Your current paid period is still active",
        message: details.retryAt
          ? `You can start this subscription after ${formatCheckoutDate(details.retryAt)}.`
          : "You can start this subscription after your current paid period ends.",
        action: "retry",
        retryAt: details.retryAt,
      };
    case "billing_provider_subscription_exists":
      return terminal(
        "A Stripe subscription already exists",
        "This account already has a managed subscription. Contact ShopExperts if you expected a different plan.",
      );
    case "billing_checkout_organization_missing":
      return terminal(
        "Account setup needs attention",
        "We couldn’t connect this expert profile to a billing account. Contact ShopExperts for help.",
      );
    case "billing_checkout_active":
      return {
        kind: "retryable",
        title: "A checkout is already open",
        message: "We’ll look for your open checkout so you can resume it.",
        action: "refresh",
        retryAt: null,
      };
    case "billing_checkout_provider_failed":
      return {
        kind: "retryable",
        title: "Stripe checkout could not be started",
        message: "Stripe did not respond. Your invitation is still safe, so you can try again.",
        action: "retry",
        retryAt: null,
      };
  }

  if (details.status === 410 || details.status === 403) {
    return terminal(
      "Invitation can’t be used",
      details.message || "Ask ShopExperts to send you a new payment invitation.",
    );
  }

  return {
    kind: "retryable",
    title: "Checkout could not be prepared",
    message: details.message || "Something interrupted the request. Your invitation is still safe, so you can try again.",
    action: "retry",
    retryAt: null,
  };
}

export function formatCheckoutAmount(checkout: ExpertOfferTerms): string {
  const option = checkout.billing_option;
  if (!option) return "Price unavailable";

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: option.currency.toUpperCase(),
    }).format(option.amount_cents / 100);
  } catch {
    return `${(option.amount_cents / 100).toFixed(2)} ${option.currency.toUpperCase()}`;
  }
}

export function formatCheckoutCadence(checkout: ExpertOfferTerms): string {
  const option = checkout.billing_option;
  if (!option) return "Billing schedule unavailable";
  if (option.interval_count === 1) return `Every ${option.interval_unit}`;
  return `Every ${option.interval_count} ${option.interval_unit}s`;
}

export function formatCheckoutDate(value: string | null | undefined): string {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}
